// useStewardshipScatter.ts
import { useEffect, useMemo, useReducer, useCallback } from "react";
import { throttle } from "lodash";
import { PaginationParams } from "../types/ScreenerTypes";
import {
  CompanyEngagementMateriality,
  StewardshipFilterParam,
} from "../models/stewardshipModels";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import {
  adaptiveCluster,
  ClusterPoint,
  DataPoint,
} from "../pages/StewardshipHomepage/EngagementMateriality/util";

export const PAGINATION_BASE_PARAMS: PaginationParams = {
  page: 1,
  page_size: 250,
};

const missingRequiredPage = (
  range: { minIndex: number; maxIndex: number },
  available: number[]
): number | null => {
  const size = PAGINATION_BASE_PARAMS.page_size;
  const req = [
    Math.floor(range.minIndex / size) + 1,
    Math.floor((range.maxIndex - 1) / size) + 1,
  ];
  return req.find((p) => !available.includes(p)) || null;
};

export type SliderMark = { minIndex: number; maxIndex: number };

type ReducerState = {
  pagination: PaginationParams;
  availablePages: number[];
  dataCache: Record<string, CompanyEngagementMateriality[]>;

  companiesSliderRange: SliderMark;
  includeNonEngaged: boolean;
  maxClusters: number;

  totalCompanies: number;
};

export enum ActionType {
  RESET = "RESET",
  SET_PAGE = "SET_PAGE",
  CACHE_DATA = "CACHE_DATA",
  SET_SLIDER_RANGE = "SET_SLIDER_RANGE",
  SET_INCLUDE_UNENGAGED = "SET_INCLUDE_UNENGAGED",
  SET_MAX_CLUSTERS = "SET_MAX_CLUSTERS",
  SET_TOTAL = "SET_TOTAL",
}

type Action =
  | { type: ActionType.RESET }
  | { type: ActionType.SET_PAGE; page: number }
  | {
      type: ActionType.CACHE_DATA;
      page: number;
      data: CompanyEngagementMateriality[];
    }
  | { type: ActionType.SET_SLIDER_RANGE; range: [number, number] }
  | { type: ActionType.SET_INCLUDE_UNENGAGED; value: boolean }
  | { type: ActionType.SET_MAX_CLUSTERS; value: number };

const initialState: ReducerState = {
  pagination: PAGINATION_BASE_PARAMS,
  availablePages: [0, 1],
  dataCache: {},
  companiesSliderRange: {
    minIndex: 0,
    maxIndex: PAGINATION_BASE_PARAMS.page_size,
  },
  includeNonEngaged: false,
  maxClusters: 4,
  totalCompanies: 0,
};

const reducer = (state: ReducerState, action: Action): ReducerState => {
  switch (action.type) {
    case ActionType.RESET: {
      return {
        ...initialState,
        includeNonEngaged: state.includeNonEngaged,
      };
    }

    case ActionType.SET_PAGE: {
      if (action.page === state.pagination.page) return state;
      return {
        ...state,
        pagination: { ...state.pagination, page: action.page },
      };
    }

    case ActionType.CACHE_DATA: {
      const newAvailablePage = action.page;
      const nextCache = {
        ...state.dataCache,
        [String(newAvailablePage)]: action.data,
      };

      const totalCompanies = action.data[0]?.total_rows || state.totalCompanies;

      const keptPages = Array.from(
        new Set(
          state.availablePages
            .filter((p) => Math.abs(p - newAvailablePage) <= 1)
            .concat(newAvailablePage)
        )
      )
        .sort((a, b) => a - b)
        .slice(-2);

      const prunedCache = Object.fromEntries(
        keptPages.map((p) => [String(p), nextCache[String(p)]])
      );

      return {
        ...state,
        availablePages: keptPages,
        dataCache: prunedCache,
        totalCompanies,
      };
    }

    case ActionType.SET_SLIDER_RANGE: {
      const [minIndex, maxIndex] = action.range;
      const companiesSliderRange = { minIndex, maxIndex };

      const missing = missingRequiredPage(
        companiesSliderRange,
        state.availablePages
      );

      return {
        ...state,
        companiesSliderRange,
        pagination: missing
          ? { ...state.pagination, page: missing }
          : state.pagination,
      };
    }

    case ActionType.SET_INCLUDE_UNENGAGED: {
      const next = action.value;
      return {
        ...initialState,
        includeNonEngaged: next,
      };
    }

    case ActionType.SET_MAX_CLUSTERS: {
      return { ...state, maxClusters: action.value };
    }

    default:
      return state;
  }
};

type useEngagementMaterialityParams = {
  filters: StewardshipFilterParam;
};

export interface UseStewardshipScatterResult {
  isLoading: boolean;

  totalCompanies: number;

  includeNonEngaged: boolean;
  setIncludeNonEngaged: (value: boolean) => void;
  maxClusters: number;
  setMaxClusters: (value: number) => void;
  maxClusterSlider: number;

  companiesSliderRange: { minIndex: number; maxIndex: number };
  updateCompaniesSliderRange: (range: [number, number]) => void;

  clusteredData: { id: string; data: ClusterPoint[] }[];
  clusteredPoints: ClusterPoint[];
  dataPoints: DataPoint[];
}

export const useStewardshipScatter = ({
  filters,
}: useEngagementMaterialityParams): UseStewardshipScatterResult => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { addToast } = useToast();
  const {
    error,
    sendData: fetchEngagementCompaniesMateriality,
    isLoading,
    data: rawData,
  } = usePost<CompanyEngagementMateriality[], StewardshipFilterParam>();

  useEffect(() => {
    if (!error) return;
    addToast("Error loading companies engagement materiality", "error");
  }, [error]);

  const updateCompaniesSliderRange = useMemo(() => {
    const fn = throttle((range: [number, number]) => {
      dispatch({ type: ActionType.SET_SLIDER_RANGE, range });
    }, 500);
    return fn;
  }, []);
  useEffect(
    () => () => updateCompaniesSliderRange.cancel(),
    [updateCompaniesSliderRange]
  );

  useEffect(() => {
    dispatch({ type: ActionType.RESET });
  }, [filters]);

  useEffect(() => {
    fetchEngagementCompaniesMateriality(
      "GET_COMPANIES_ENGAGEMENT_MATERIALITY",
      {
        ...filters,
        include_unengaged: state.includeNonEngaged,
        pagination: state.pagination,
      }
    );
  }, [filters, state.includeNonEngaged, state.pagination]);

  useEffect(() => {
    if (!rawData?.length) return;
    dispatch({
      type: ActionType.CACHE_DATA,
      page: state.pagination.page,
      data: rawData,
    });
  }, [rawData, state.pagination.page, state.totalCompanies]);

  const dataPoints = useMemo(() => {
    const orderedPages = Object.keys(state.dataCache)
      .map(Number)
      .sort((a, b) => a - b);

    const flatRows = orderedPages.flatMap(
      (p) => state.dataCache[String(p)] || []
    );

    const minCachedPage = orderedPages[0] ?? state.pagination.page;
    const offset =
      (Math.max(minCachedPage, 1) - 1) * state.pagination.page_size;

    const { minIndex, maxIndex } = state.companiesSliderRange;
    const windowData = flatRows.slice(minIndex - offset, maxIndex - offset);

    const dataPoints: DataPoint[] = windowData.map((c) => ({
      id: c.compass_cid,
      x: c.weight,
      y: c.coverage,
      ...c,
    }));

    return dataPoints;
  }, [state.dataCache, state.companiesSliderRange]);

  const { clusteredPoints, clusteredData, maxClusterSlider } = useMemo(() => {
    const points = adaptiveCluster(dataPoints, state.maxClusters);

    const slider = Math.max(4, Math.floor((dataPoints.length || 4) / 2));

    return {
      clusteredPoints: points,
      clusteredData: [
        {
          id: "Stewardship Entities",
          data: points,
        },
      ] as { id: string; data: ClusterPoint[] }[],
      maxClusterSlider: slider,
    };
  }, [dataPoints, state.maxClusters]);

  const setIncludeNonEngaged = useCallback(
    (value: boolean) =>
      dispatch({ type: ActionType.SET_INCLUDE_UNENGAGED, value }),
    []
  );
  const setMaxClusters = useCallback(
    (value: number) => dispatch({ type: ActionType.SET_MAX_CLUSTERS, value }),
    []
  );

  return {
    isLoading,

    totalCompanies: state.totalCompanies,

    includeNonEngaged: state.includeNonEngaged,
    setIncludeNonEngaged,
    maxClusters: state.maxClusters,
    setMaxClusters,
    maxClusterSlider,

    companiesSliderRange: state.companiesSliderRange,
    updateCompaniesSliderRange,

    clusteredData,
    clusteredPoints,
    dataPoints,
  };
};
